import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, IconButton, Button, TextField, Paper, Grid,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel,
  Stack, Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PrintIcon from '@mui/icons-material/Print';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
  getStudyOrders, createStudyOrder, updateStudyOrder, deleteStudyOrder,
  emitStudyOrder, downloadStudyOrderPdf, getStudyOrderTemplates, logPatientMessage,
} from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useClinicFeatures } from '../../hooks/useClinicFeatures';
import { useNotification } from '../../hooks/useNotification';
import { tryParseJson, STATUS_RX, fmtDate, openWhatsApp } from './patientProfileHelpers';

const CATEGORY_OPTIONS = [
  { value: 'laboratorio', label: 'Laboratorio' },
  { value: 'imagen', label: 'Imagen' },
  { value: 'otro', label: 'Otro' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map(c => [c.value, c.label]));

const emptyOrderItem = () => ({ study_name: '', category: 'laboratorio', instructions: '' });

const ItemRow = ({ item, idx, onUpdate, onRemove, canRemove }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
    <Grid container spacing={1.5} alignItems="flex-start">
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField label="Estudio *" size="small" fullWidth
          placeholder="ej: Laboratorio completo"
          value={item.study_name}
          onChange={e => onUpdate(idx, 'study_name', e.target.value)} />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Categoría</InputLabel>
          <Select value={item.category} label="Categoría"
            onChange={e => onUpdate(idx, 'category', e.target.value)}>
            {CATEGORY_OPTIONS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField label="Indicaciones" size="small" fullWidth
          placeholder="ej: En ayunas de 8hs"
          value={item.instructions}
          onChange={e => onUpdate(idx, 'instructions', e.target.value)} />
        {canRemove && (
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Grid>
    </Grid>
  </Paper>
);

// Lista de estudios de una orden expandida. En mobile una tabla de 3
// columnas ya entra razonablemente, pero se lleva al mismo patrón de línea
// que RecetasTab.js/Recetas.js para consistencia visual entre las 3
// pantallas que comparten esta misma estructura de card expandible.
const StudyList = ({ items, isMobile }) => {
  if (!isMobile) {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Estudio</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Categoría</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Indicaciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell><Typography variant="body2" fontWeight={600}>{item.study_name}</Typography></TableCell>
              <TableCell><Typography variant="body2">{CATEGORY_LABEL[item.category] || item.category || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.instructions || '—'}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  return (
    <Stack spacing={1}>
      {items.map(item => (
        <Box key={item.id} sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>{item.study_name}</Typography>
            <Chip label={CATEGORY_LABEL[item.category] || item.category || '—'} size="small" variant="outlined"
              sx={{ height: 18, fontSize: '0.62rem' }} />
          </Box>
          {item.instructions && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.instructions}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

// ── Tab: Órdenes de estudio ────────────────────────────────────────────────────
export const OrdenesTab = ({ patient, doctors = [] }) => {
  const { isAdmin, isMedico, doctorId } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  const canWrite = isAdmin || isMedico;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Form state
  const [formOpen, setFormOpen]           = useState(false);
  const [editingOrder, setEditingOrder]   = useState(null);
  const [formDoctorId, setFormDoctorId]   = useState('');
  const [formIndication, setFormIndication] = useState('');
  const [formNotes, setFormNotes]         = useState('');
  const [formItems, setFormItems]         = useState([emptyOrderItem()]);
  const [formError, setFormError]         = useState('');
  const [saving, setSaving]               = useState(false);
  const [templateMenuId, setTemplateMenuId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStudyOrders({ patient_id: patient.id, limit: 100 });
      setOrders(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getStudyOrderTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const updateItem = useCallback((idx, field, value) =>
    setFormItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it)),
  []);

  const openCreate = () => {
    setEditingOrder(null);
    setFormDoctorId(isMedico && doctorId ? String(doctorId) : '');
    setFormIndication(''); setFormNotes('');
    setFormItems([emptyOrderItem()]);
    setFormError('');
    setTemplateMenuId('');
    setFormOpen(true);
  };

  const openEdit = (order) => {
    setEditingOrder(order);
    setFormDoctorId(String(order.doctor_id));
    setFormIndication(order.clinical_indication || '');
    setFormNotes(order.notes || '');
    setFormItems(
      order.items.length > 0
        ? order.items.map(i => ({ study_name: i.study_name, category: i.category || 'otro', instructions: i.instructions || '' }))
        : [emptyOrderItem()]
    );
    setFormError('');
    setTemplateMenuId('');
    setFormOpen(true);
  };

  // Aplicar plantilla: agrega los items de la plantilla a los ya cargados
  // (reemplaza la fila vacía inicial si es la única) — a diferencia de las
  // plantillas de consulta, acá "aplicar" es agregar filas, no hacer merge
  // de campos de un form.
  const applyTemplate = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    const items = tryParseJson(tmpl.content, []);
    if (items.length === 0) return;
    const newItems = items.map(i => ({
      study_name: i.study_name || '', category: i.category || 'otro', instructions: i.instructions || '',
    }));
    setFormItems(prev => {
      const onlyEmptyRow = prev.length === 1 && !prev[0].study_name.trim();
      return onlyEmptyRow ? newItems : [...prev, ...newItems];
    });
    setTemplateMenuId('');
  };

  const handleSave = async (andEmit = false) => {
    if (!formDoctorId) { setFormError('Seleccioná un médico'); return; }
    const validItems = formItems.filter(i => i.study_name.trim());
    if (validItems.length === 0) { setFormError('Agregá al menos un estudio'); return; }
    const payload = validItems.map(({ study_name, category, instructions }) => ({ study_name, category, instructions }));

    setSaving(true); setFormError('');
    let orderId = editingOrder?.id;
    try {
      if (editingOrder) {
        await updateStudyOrder(editingOrder.id, { clinical_indication: formIndication, notes: formNotes, items: payload });
      } else {
        const res = await createStudyOrder({
          patient_id: patient.id,
          doctor_id: Number(formDoctorId),
          clinical_indication: formIndication,
          notes: formNotes,
          items: payload,
        });
        orderId = res.data.id;
        // Igual criterio que RecetasTab: si la emisión de abajo falla, pasar
        // a modo "editar" sobre esta orden ya creada en vez de dejar el
        // diálogo en modo "crear" (evita duplicados en un reintento).
        setEditingOrder({ id: orderId });
      }
      if (andEmit) await emitStudyOrder(orderId);
      setFormOpen(false);
      await load();
    } catch (err) {
      if (andEmit && orderId) {
        setFormError((err.userMessage || 'No se pudo emitir la orden') + ' — la orden quedó guardada como borrador, podés reintentar emitirla.');
      } else {
        setFormError(err.userMessage || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEmit = async (order) => {
    if (!window.confirm('¿Emitir esta orden? No podrá editarse después.')) return;
    try { await emitStudyOrder(order.id); await load(); }
    catch (err) { notify(err.userMessage || 'No se pudo emitir la orden', 'error'); }
  };

  const handleDelete = async (order) => {
    if (!window.confirm('¿Eliminar esta orden?')) return;
    try { await deleteStudyOrder(order.id); await load(); }
    catch (err) { notify(err.userMessage || 'No se pudo eliminar la orden', 'error'); }
  };

  const handleWhatsAppOrder = async (order) => {
    if (!patient.phone) {
      notify('El paciente no tiene número de teléfono registrado', 'warning');
      return;
    }
    const verifyUrl = `${window.location.origin}/verify?token=${encodeURIComponent(order.signature)}&type=study_order`;
    const items = order.items.map(i => `• ${i.study_name}${i.instructions ? ` — ${i.instructions}` : ''}`).join('\n');
    const text = `Hola ${patient.first_name || patient.name}, te envío tu orden de estudio del ${fmtDate(order.created_at)}.\n\n${items}\n\nVerificá tu orden en: ${verifyUrl}`;
    openWhatsApp(patient.phone, text);
    try {
      await logPatientMessage(patient.id, {
        channel: 'whatsapp',
        message_type: 'otro',
        message_preview: `Orden de estudio del ${fmtDate(order.created_at)} — ${order.items.map(i => i.study_name).join(', ')}`,
      });
      notify('WhatsApp abierto', 'success');
    } catch {
      notify('WhatsApp abierto, pero no se pudo registrar en el historial de comunicaciones', 'warning');
    }
  };

  const handlePdf = async (order) => {
    try {
      const res = await downloadStudyOrderPdf(order.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      notify(err.userMessage || 'No se pudo generar el PDF', 'error');
    }
  };

  if (!features.study_orders) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary" gutterBottom>
          El módulo de órdenes de estudio no está habilitado.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Activalo desde Configuración → Módulos.
        </Typography>
      </Box>
    );
  }

  const doctorById = (id) => doctors.find(d => d.id === id);
  const selectedDoctor = formDoctorId ? doctorById(Number(formDoctorId)) : null;
  const canEmitForm    = selectedDoctor?.matricula && formItems.some(i => i.study_name.trim());

  return (
    <Box sx={{ mt: 2 }}>
      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Nueva orden
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : orders.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          Sin órdenes de estudio registradas para este paciente.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {orders.map(order => {
            const st     = STATUS_RX[order.status] || STATUS_RX.borrador;
            const doc    = doctorById(order.doctor_id);
            const isOpen = expanded === order.id;
            const isBorr = order.status === 'borrador';
            return (
              <Paper key={order.id} variant="outlined" sx={{ overflow: 'hidden' }}>
                {/* Header — mismo criterio responsive que RecetasTab: flexWrap
                    para que el bloque de acciones caiga a su propia fila en
                    mobile en vez de apretarse. */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, rowGap: 0.5, flexWrap: 'wrap', px: 2, py: 1.5, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : order.id)}
                >
                  <Chip label={st.label} size="small"
                    sx={{ bgcolor: st.color, color: 'white', fontWeight: 700, minWidth: 74, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                    {fmtDate(order.created_at)}
                  </Typography>
                  {doc && (
                    <Typography variant="body2" color="text.secondary" noWrap
                      sx={{ flexShrink: 1, minWidth: 0, maxWidth: 160 }}>
                      Dr. {doc.name}
                    </Typography>
                  )}
                  {order.clinical_indication && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ flex: 1, minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.clinical_indication}
                    </Typography>
                  )}
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.disabled">{order.items.length} est.</Typography>
                    {canWrite && isBorr && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); openEdit(order); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Emitir orden">
                          <IconButton size="small" color="success"
                            onClick={e => { e.stopPropagation(); handleEmit(order); }}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error"
                            onClick={e => { e.stopPropagation(); handleDelete(order); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(order.status === 'emitida' || order.status === 'vencida') && (
                      <>
                        {features?.whatsapp && (
                          <Tooltip title="Enviar orden por WhatsApp">
                            <IconButton size="small" sx={{ color: '#25D366' }}
                              onClick={e => { e.stopPropagation(); handleWhatsAppOrder(order); }}>
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Imprimir / descargar PDF">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); handlePdf(order); }}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <ExpandMoreIcon sx={{
                      fontSize: 18, color: 'text.disabled',
                      transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none',
                    }} />
                  </Box>
                </Box>

                {isOpen && (
                  <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {order.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        <strong>Notas:</strong> {order.notes}
                      </Typography>
                    )}
                    <StudyList items={order.items} isMobile={isMobile} />
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Dialog crear / editar */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingOrder ? 'Editar orden de estudio' : 'Nueva orden de estudio'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" disabled={isMedico}>
                <InputLabel>Médico *</InputLabel>
                <Select value={formDoctorId} label="Médico *"
                  onChange={e => setFormDoctorId(e.target.value)}>
                  {doctors.map(d => (
                    <MenuItem key={d.id} value={String(d.id)}>
                      Dr. {d.name}{d.matricula ? ` — Mat. ${d.matricula}` : ' ⚠ sin matrícula'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedDoctor && !selectedDoctor.matricula && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  Sin matrícula — no se podrá emitir hasta que la cargue.
                </Alert>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Diagnóstico / Indicación clínica" fullWidth size="small"
                value={formIndication} onChange={e => setFormIndication(e.target.value)} />
            </Grid>
            <Grid size={12}>
              <TextField label="Notas adicionales" fullWidth size="small" multiline rows={2}
                value={formNotes} onChange={e => setFormNotes(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Estudios
            </Typography>
            {templates.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Usar plantilla</InputLabel>
                <Select
                  value={templateMenuId} label="Usar plantilla"
                  startAdornment={<ContentPasteIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.disabled' }} />}
                  onChange={e => applyTemplate(e.target.value)}
                >
                  {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>

          {formItems.map((item, idx) => (
            <ItemRow
              key={idx}
              item={item}
              idx={idx}
              onUpdate={updateItem}
              onRemove={() => setFormItems(prev => prev.filter((_, i) => i !== idx))}
              canRemove={formItems.length > 1}
            />
          ))}

          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setFormItems(prev => [...prev, emptyOrderItem()])}>
            Agregar estudio
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Guardar borrador'}
          </Button>
          <Tooltip title={!selectedDoctor?.matricula ? 'El médico no tiene matrícula cargada' : ''}>
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={() => handleSave(true)}
                disabled={saving || !canEmitForm}
              >
                {saving ? <CircularProgress size={16} /> : 'Guardar y emitir'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
